import {
  Table,
  Tag,
  Popconfirm,
  Pagination,
  Tooltip,
  message,
  Image,
  Spin,
  Switch,
  Select,
  Card,
  Empty,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { IoTrashOutline } from "react-icons/io5";
import { PlayCircleOutlined } from "@ant-design/icons";
import { s3FallbackUrl } from "../../../utils/mediaFallback";
import { useState } from "react";
import moment from "moment";
import "../styles.css";

const { Option } = Select;

function DataTable(props: any) {
  const [loadingRows, setLoadingRows] = useState<{ [key: number]: boolean }>(
    {}
  );

  interface DataType {
    key: string;
    id: number;
    video: string;
    title: string;
    head: string;
    description: string;
    createdAt: string;
    publishedAt: string;
    tags: any[];
    user: any;
    is_deleted: boolean;
  }

  const columns: ColumnsType<DataType> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 50,
      align: "left",
      render: (_text, _record, index) => {
        return <div style={{ textAlign: "left" }}>{index + 1}</div>;
      },
    },
    {
      title: "Cover",
      dataIndex: "head",
      key: "head",
      width: 70,
      align: "center",
      render: (_text, record: any) => {
        const imageUrl = record.head;
        return imageUrl ? (
          <Image
            src={imageUrl}
            width={50}
            height={50}
            style={{ borderRadius: 6, objectFit: "cover" }}
            preview={{ mask: null }}
            fallback={s3FallbackUrl(imageUrl) || undefined}
          />
        ) : (
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: 6,
              background: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
              fontSize: 11,
              color: "#bbb",
            }}
          >
            No img
          </div>
        );
      },
    },
    {
      title: "Video Thumbnail",
      key: "video",
      dataIndex: "video",
      align: "center",
      width: 120,
      render: (_text, record: any) => {
        const videoUrl = record.video || record.attributes?.video;
        return (
          <div
            style={{
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {videoUrl ? (
              <div
                style={{
                  position: "relative",
                  width: 70,
                  height: 70,
                  backgroundColor: "#F5F5F5",
                  borderRadius: 6,
                  border: "1px solid #e0e0e0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                onClick={() => window.open(videoUrl, "_blank")}
              >
                <PlayCircleOutlined style={{ fontSize: 24, color: "#666" }} />
              </div>
            ) : (
              <div className="homescreen-table-NoImg">No video</div>
            )}
          </div>
        );
      },
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
      align: "center",
      render: (_text, record: any) => {
        return (
          <div
            style={{ fontSize: 13, fontWeight: 500, textAlign: "center" }}
          >
            {record.title || "Untitled"}
          </div>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 300,
      align: "center",
      render: (_text, record: any) => {
        const desc = record.description || "";
        const truncated =
          desc.length > 100 ? desc.substring(0, 100) + "..." : desc;
        return (
          <div style={{ fontSize: 12, color: "#666", textAlign: "center" }}>
            {truncated || "N/A"}
          </div>
        );
      },
    },
    {
      title: "Created By",
      dataIndex: "user",
      key: "user",
      width: 180,
      align: "center",
      render: (user: any) => {
        return (
          <div style={{ fontSize: "12px", textAlign: "center" }}>
            {user?.username || user?.attributes?.username || "N/A"}
          </div>
        );
      },
    },
    {
      title: "Published At",
      dataIndex: "publishedAt",
      key: "publishedAt",
      width: 200,
      align: "center",
      render: (_text, record: any) => {
        return (
          <div style={{ fontSize: 12, textAlign: "center" }}>
            {record?.publishedAt ? (
              moment(record.publishedAt).format("DD-MM-YYYY-h:mm A")
            ) : (
              <span style={{ color: "red" }}>Not Published</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      align: "center",
      render: (_text, record: any) => {
        const isPublished = !!record?.publishedAt && !record?.is_deleted;
        const isLoading = loadingRows[record.id] || false;

        if (record?.is_deleted === true) {
          return (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Tag color="red" style={{ fontSize: "10px", margin: 0 }}>
                trash
              </Tag>
            </div>
          );
        }

        return (
          <div
            className="highlights-toggle-switch"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Tooltip
              title={isPublished ? "Click to unpublish" : "Click to publish"}
            >
              <Switch
                checked={isPublished}
                loading={isLoading}
                disabled={isLoading}
                size="small"
                checkedChildren={<span style={{ fontSize: "10px" }}>Published</span>}
                unCheckedChildren={<span style={{ fontSize: "10px" }}>Draft</span>}
                style={{ minWidth: "60px" }}
                className="highlights-smooth-switch"
                onChange={(checked) => {
                  if (props.onTogglePublish) {
                    setLoadingRows((prev) => ({
                      ...prev,
                      [record.id]: true,
                    }));
                    props.onTogglePublish(record, checked, () => {
                      setLoadingRows((prev) => {
                        const newState = { ...prev };
                        delete newState[record.id];
                        return newState;
                      });
                    });
                  }
                }}
              />
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Tags",
      key: "tags",
      dataIndex: "tags",
      width: 200,
      align: "center",
      render: (tags: any[], record: any) => {
        if (!tags || tags.length === 0) {
          return (
            <span
              style={{
                fontSize: "12px",
                color: "#999",
                textAlign: "center",
                display: "block",
              }}
            >
              No tags
            </span>
          );
        }
        const tagArray = Array.isArray(tags) ? tags : [];
        return (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "4px",
              justifyContent: "center",
            }}
          >
            {tagArray.slice(0, 2).map((tag: any, index: number) => {
              let tagName = "Tag";
              if (typeof tag === "string") {
                tagName = tag;
              } else if (tag?.tag) {
                tagName = tag.tag;
              } else if (tag?.attributes?.tag) {
                tagName = tag.attributes.tag;
              } else if (tag?.name) {
                tagName = tag.name;
              } else if (tag?.attributes?.name) {
                tagName = tag.attributes.name;
              }
              return (
                <Tag
                  key={`${record?.id || index}-${index}`}
                  color="blue"
                  style={{ fontSize: "10px", margin: 0 }}
                >
                  {String(tagName)}
                </Tag>
              );
            })}
            {tagArray.length > 2 && (
              <Tag
                color="default"
                style={{ fontSize: "10px", margin: 0 }}
              >
                +{tagArray.length - 2}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      align: "center",
      render: (_, record: any) => {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <Popconfirm
              title="Delete reel"
              description="Are you sure to delete this reel?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => {
                if (props.onDelete) {
                  props.onDelete(record);
                }
              }}
            >
                <IoTrashOutline
                  color="red"
                  size={18}
                  style={{ cursor: "pointer" }}
                />
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  return (
    <Card
      style={{
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        marginTop: 10,
      }}
      bodyStyle={{ padding: 0 }}
    >
      <Table
        columns={columns}
        dataSource={props.data}
        className="commen-table-custom-scrollbar"
        pagination={false}
        size="small"
        rowKey={(record: any) => String(record?.id || Math.random())}
        rowClassName={(_record, index) =>
          index % 2 === 0 ? "table-row-even" : "table-row-odd"
        }
        locale={{
          emptyText: <Empty description="No reels found" />,
        }}
      />
      <div
        style={{
          borderTop: "1px solid #f0f0f0",
          padding: "12px 16px",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {props.loading ? (
          <Spin size="small" />
        ) : (
          <>
            <Pagination
              responsive
              current={props?.page}
              total={props.meta && props.meta}
              pageSize={props?.pagesize}
              onChange={(p, t) => {
                props.onPageChange(p, t);
              }}
            />
            <Select
              placeholder="Page size"
              style={{ width: 100 }}
              value={props?.pagesize}
              onChange={(value) => {
                props.onPageChange(props?.page, value);
              }}
            >
              <Option value={10}>10</Option>
              <Option value={25}>25</Option>
              <Option value={50}>50</Option>
              <Option value={100}>100</Option>
            </Select>
          </>
        )}
      </div>
    </Card>
  );
}

export default DataTable;
